import { Layers, Pencil, Plus, Sliders, Trash2 } from 'lucide-react';
import { useMemo, useState } from 'react';
import { EmptyState } from '../../../components/EmptyState.js';
import { CardGridSkeleton } from '../../../components/Skeletons.js';
import { Button } from '../../../components/ui/button.js';
import { KpiChip } from '../components/KpiChip.js';
import { KpiEditorDialog, type KpiEditorValues } from '../components/KpiEditorDialog.js';
import {
  useCreateKpiType,
  useDeleteKpiType,
  useKpiMetrics,
  useKpiTypes,
  useUpdateKpiType,
} from '../hooks.js';
import { asKpiMetric, asKpiType, type KpiType } from '../types.js';

/**
 * KPI types catalog. Mirrors the legacy
 * `wf-audit/src/slices/kpi/pages/types.tsx` — colourful card grid where
 * every type carries a glyph + brand colour + the count of metrics
 * attached.
 *
 * Inline-edit affordances: each card surfaces a hover-only
 * pencil + trash button. Pencil opens `KpiEditorDialog` against the
 * current type; trash opens a delete confirmation.
 */
export function KpiTypesPage(): JSX.Element {
  const { data: typeRows = [], isLoading, error } = useKpiTypes();
  const { data: metricRows = [] } = useKpiMetrics();

  const types = useMemo(() => typeRows.map(asKpiType), [typeRows]);
  const metrics = useMemo(() => metricRows.map(asKpiMetric), [metricRows]);
  const metricCountByType = useMemo(() => {
    const m = new Map<number, number>();
    for (const metric of metrics) {
      if (metric.kpiTypeId === null) continue;
      m.set(metric.kpiTypeId, (m.get(metric.kpiTypeId) ?? 0) + 1);
    }
    return m;
  }, [metrics]);
  const orphanCount = useMemo(() => metrics.filter((m) => m.kpiTypeId === null).length, [metrics]);

  const create = useCreateKpiType();
  const update = useUpdateKpiType();
  const remove = useDeleteKpiType();

  const [editor, setEditor] = useState<{
    open: boolean;
    target: KpiType | null;
  }>({ open: false, target: null });
  const [deleting, setDeleting] = useState<KpiType | null>(null);

  const openCreate = (): void => setEditor({ open: true, target: null });
  const openEdit = (t: KpiType): void => setEditor({ open: true, target: t });
  const closeEditor = (): void => setEditor({ open: false, target: null });

  const onSubmit = async (values: KpiEditorValues): Promise<void> => {
    if (values.kind !== 'type') return;
    if (editor.target) {
      await update.mutate({
        id: editor.target.id,
        input: { name: values.name, color: values.color, icon: values.icon },
      });
    } else {
      await create.mutate({ name: values.name, color: values.color, icon: values.icon });
    }
    closeEditor();
  };

  const onConfirmDelete = async (): Promise<void> => {
    if (!deleting) return;
    await remove.mutate(deleting.id);
    setDeleting(null);
  };

  const initial: KpiEditorValues = editor.target
    ? {
        kind: 'type',
        name: editor.target.name,
        color: editor.target.color,
        icon: editor.target.icon,
      }
    : { kind: 'type', name: '', color: '#3b82f6', icon: 'bar-chart' };

  return (
    <div className="relative z-10 flex h-full flex-col">
      <header className="flex items-center justify-between px-6 pb-2 pt-6">
        <div>
          <h1 className="text-2xl font-light text-foreground">KPI Types</h1>
          <p className="text-sm text-foreground/60">
            Group metrics under a shared type. Used to colour-code charts and roll up rollups.
          </p>
        </div>
        <Button
          type="button"
          onClick={openCreate}
          className="bg-[color:var(--brand-yellow)] text-[color:var(--gradient-end)] hover:opacity-90"
        >
          <Plus className="mr-1.5 h-4 w-4" /> New type
        </Button>
      </header>

      <div className="px-6 py-4">
        {error ? (
          <div className="rounded-md border border-rose-400/30 bg-rose-500/10 p-4 text-sm text-rose-800 dark:text-rose-200">
            Could not load KPI types: {error.message}
          </div>
        ) : isLoading ? (
          <CardGridSkeleton rows={2} columns={4} />
        ) : types.length === 0 ? (
          <EmptyState
            icon={Layers}
            tint="violet"
            title="No KPI types defined yet"
            description="KPI types are the colourful groupings (Efficiency / Quality / Volume / Cost…) that scenarios surface in the value rollup. Define one to start attaching metrics."
          />
        ) : (
          <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
            {types.map((t) => (
              <article
                key={t.id}
                className="group relative flex items-center gap-3 rounded-md border border-foreground/10 bg-foreground/[0.03] p-4"
              >
                <KpiChip icon={t.icon} color={t.color} size="lg" />
                <div className="min-w-0 flex-1">
                  <h2 className="truncate text-base font-semibold text-foreground">{t.name}</h2>
                  <p className="text-xs text-foreground/60">
                    {metricCountByType.get(t.id) ?? 0} metric
                    {(metricCountByType.get(t.id) ?? 0) === 1 ? '' : 's'}
                  </p>
                </div>
                <div className="absolute right-2 top-2 hidden gap-1 group-hover:flex">
                  <button
                    type="button"
                    onClick={() => openEdit(t)}
                    aria-label={`Edit ${t.name}`}
                    title="Edit"
                    className="rounded p-1 text-foreground/60 transition-colors hover:bg-foreground/10 hover:text-foreground"
                  >
                    <Pencil className="h-3.5 w-3.5" />
                  </button>
                  <button
                    type="button"
                    onClick={() => setDeleting(t)}
                    aria-label={`Delete ${t.name}`}
                    title="Delete"
                    className="rounded p-1 text-foreground/60 transition-colors hover:bg-rose-500/20 hover:text-rose-800 dark:hover:text-rose-200"
                  >
                    <Trash2 className="h-3.5 w-3.5" />
                  </button>
                </div>
              </article>
            ))}
          </div>
        )}

        {orphanCount > 0 ? (
          <div className="mt-4 flex items-center gap-2 rounded-md border border-amber-400/30 bg-amber-500/10 p-3 text-xs text-amber-800 dark:text-amber-200">
            <Sliders className="h-4 w-4" />
            {orphanCount} metric{orphanCount === 1 ? '' : 's'} not attached to any type — head to{' '}
            <a href="/kpi" className="underline">
              KPI Metrics
            </a>{' '}
            to organise.
          </div>
        ) : null}
      </div>

      <KpiEditorDialog
        open={editor.open}
        onOpenChange={(open) => (open ? null : closeEditor())}
        initial={initial}
        onSubmit={onSubmit}
        title={editor.target ? `Edit "${editor.target.name}"` : 'New KPI type'}
        submitting={create.isPending || update.isPending}
        error={(editor.target ? update.error : create.error) ?? undefined}
      />

      <DeleteConfirmDialog
        target={deleting}
        targetLabel="KPI type"
        descriptionSuffix={
          deleting
            ? ` Attached metrics (${metricCountByType.get(deleting.id) ?? 0}) will be detached but not deleted.`
            : ''
        }
        submitting={remove.isPending}
        onCancel={() => setDeleting(null)}
        onConfirm={onConfirmDelete}
      />
    </div>
  );
}

/**
 * Tiny delete-confirm dialog inlined so each consumer can shape the
 * copy. Kept in this file because it's only used by the two KPI
 * pages today; promote to a shared component if a third caller
 * lands.
 */
function DeleteConfirmDialog({
  target,
  targetLabel,
  descriptionSuffix,
  submitting,
  onCancel,
  onConfirm,
}: {
  target: { id: number; name?: string; displayName?: string } | null;
  targetLabel: string;
  descriptionSuffix?: string;
  submitting?: boolean;
  onCancel: () => void;
  onConfirm: () => Promise<void> | void;
}): JSX.Element | null {
  if (!target) return null;
  const displayName = target.name ?? target.displayName ?? `#${target.id}`;
  return (
    <div
      role="dialog"
      aria-modal="true"
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 p-4"
      onClick={onCancel}
    >
      <div
        className="max-w-md rounded-md border border-foreground/15 bg-[color:var(--surface-overlay)] p-6 text-foreground"
        onClick={(e) => e.stopPropagation()}
      >
        <h3 className="text-lg font-medium">Delete {targetLabel}?</h3>
        <p className="mt-2 text-sm text-foreground/70">
          You're about to delete <span className="font-semibold">"{displayName}"</span>.
          {descriptionSuffix}
        </p>
        <div className="mt-4 flex justify-end gap-2">
          <Button
            type="button"
            variant="outline"
            onClick={onCancel}
            className="border-foreground/15 text-foreground/70"
            disabled={submitting}
          >
            Cancel
          </Button>
          <Button
            type="button"
            onClick={() => void onConfirm()}
            disabled={submitting}
            className="bg-rose-500 text-white hover:bg-rose-600"
          >
            {submitting ? 'Deleting…' : 'Delete'}
          </Button>
        </div>
      </div>
    </div>
  );
}
